// src/pages/public/Golf.tsx
// Public golf tournament details page. Golf is open to everyone (not employee-
// gated), so this page is public. Gated on the event's golfEnabled flag — shows
// a "coming soon" state when off, mirroring Schedule.tsx.
//
// Content is admin-editable via event.golf (EventEditor → Golf Tournament
// Details). When a field is empty or the whole golf object is absent, we fall
// back to DEFAULT_GOLF so an unconfigured event still renders a complete page.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_GOLF, type GolfConfig } from "../../data/golfDefaults";

interface EventDoc {
  golfEnabled?: boolean;
  golf?: GolfConfig;
}

export default function Golf() {
  const [event, setEvent] = useState<EventDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/public/event?year=2026")
      .then(r => (r.ok ? r.json() : null))
      .then((d: EventDoc | null) => { setEvent(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Disabled / not configured — friendly placeholder (matches Schedule.tsx).
  if (!loading && event && event.golfEnabled === false) {
    return (
      <section className="section">
        <div className="container disabled-page">
          <span className="eyebrow">Coming Soon</span>
          <h1>Golf details aren't posted yet</h1>
          <p>Check back closer to the event for the tournament lineup.</p>
          <Link to="/" className="btn-back">Back to home</Link>
          <style>{`
            .disabled-page { max-width: 640px; padding: 4rem 0; }
            .disabled-page h1 { font-family: var(--font-display); font-weight: 700; text-transform: uppercase; margin: 0.5rem 0 1rem; }
            .disabled-page p { color: var(--steel); margin-bottom: 2rem; font-size: 1.05rem; }
            .eyebrow { font-family: var(--font-display); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.35em; text-transform: uppercase; color: var(--red); }
            .btn-back {
              display: inline-block; font-family: var(--font-display); font-weight: 700;
              font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase;
              color: var(--white); background: var(--red); padding: 0.85rem 2rem;
              text-decoration: none;
            }
          `}</style>
        </div>
      </section>
    );
  }

  // Merge config over defaults — blank scalars fall back; arrays fall back only
  // when absent/empty (an admin clearing a list shows defaults rather than a
  // bare section, which reads better for this content).
  const g = event?.golf;
  const description = g?.description?.trim() || DEFAULT_GOLF.description;
  const courseName = g?.courseName?.trim() || DEFAULT_GOLF.courseName;
  const courseLocation = g?.courseLocation?.trim() || DEFAULT_GOLF.courseLocation;
  const arrivalTime = g?.arrivalTime?.trim() || DEFAULT_GOLF.arrivalTime;
  const shotgunTime = g?.shotgunTime?.trim() || DEFAULT_GOLF.shotgunTime;
  const costNote = g?.costNote?.trim() || DEFAULT_GOLF.costNote;
  const signupNote = g?.signupNote?.trim() || DEFAULT_GOLF.signupNote;
  const foodNote = g?.foodNote?.trim() || DEFAULT_GOLF.foodNote;
  const competitionsIntro = g?.competitionsIntro?.trim() || DEFAULT_GOLF.competitionsIntro;
  const prizesIntro = g?.prizesIntro?.trim() || DEFAULT_GOLF.prizesIntro;
  const competitions = g?.competitions?.length ? g.competitions : DEFAULT_GOLF.competitions;
  const prizes = g?.prizes?.length ? g.prizes : DEFAULT_GOLF.prizes;

  return (
    <>
      <style>{`
        .golf-page { min-height: 100vh; background: var(--bg); }
        .golf-hero {
          background: var(--black); color: var(--white);
          padding: 5rem 4rem 4rem; position: relative; overflow: hidden;
        }
        .golf-hero::before {
          content: ''; position: absolute; inset: 0;
          background: repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(255,255,255,0.02) 12px, rgba(255,255,255,0.02) 24px);
          pointer-events: none;
        }
        .hero-inner { max-width: 1100px; margin: 0 auto; position: relative; }
        .eyebrow {
          font-family: var(--font-display); font-size: 0.72rem; font-weight: 500;
          letter-spacing: 0.35em; text-transform: uppercase; color: var(--red);
          margin-bottom: 0.75rem;
        }
        .golf-hero h1 {
          font-family: var(--font-display); font-size: clamp(2.4rem, 5vw, 3.6rem);
          font-weight: 700; text-transform: uppercase; line-height: 1; margin: 0;
        }
        .hero-desc {
          font-family: var(--font-body); font-size: 1.05rem; color: rgba(255,255,255,0.75);
          margin-top: 1.5rem; max-width: 60ch; line-height: 1.6;
        }
        .golf-body { max-width: 1100px; margin: 0 auto; padding: 4rem; }
        @media (max-width: 900px) {
          .golf-body { padding: 3rem 1.5rem; }
          .golf-hero { padding: 3.5rem 1.5rem 3rem; }
        }

        /* Key facts */
        .facts-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1px; background: var(--border); border: 1px solid var(--border);
          margin-bottom: 3rem;
        }
        .fact {
          background: var(--white); padding: 1.5rem 1.5rem;
          display: flex; flex-direction: column; gap: 0.35rem;
        }
        .fact-label {
          font-family: var(--font-display); font-size: 0.65rem; font-weight: 500;
          letter-spacing: 0.2em; text-transform: uppercase; color: var(--red);
        }
        .fact-value {
          font-family: var(--font-display); font-size: 1.15rem; font-weight: 700;
          text-transform: uppercase; color: var(--black); line-height: 1.2;
        }
        .fact-sub { font-family: var(--font-body); font-size: 0.85rem; color: var(--muted); }

        .section-eyebrow {
          font-family: var(--font-display); font-size: 0.7rem; font-weight: 500;
          letter-spacing: 0.25em; text-transform: uppercase; color: var(--red);
          margin: 0 0 1.25rem;
        }
        .golf-section { margin-bottom: 3rem; }
        .golf-section > p {
          font-family: var(--font-body); font-size: 0.98rem; color: var(--steel);
          line-height: 1.6; margin: 0 0 1rem; max-width: 65ch;
        }

        /* Competition / prize cards */
        .card-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 1px; background: var(--border); border: 1px solid var(--border);
        }
        .mini-card {
          background: var(--white); padding: 1.5rem;
          display: flex; flex-direction: column; gap: 0.4rem;
        }
        .mini-card h3 {
          font-family: var(--font-display); font-size: 1.1rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.02em; color: var(--black);
          margin: 0; line-height: 1.2;
        }
        .mini-card p { font-family: var(--font-body); font-size: 0.88rem; color: var(--steel); margin: 0; line-height: 1.5; }

        .prize-list {
          list-style: none; margin: 0; padding: 0;
          border: 1px solid var(--border);
        }
        .prize-list li {
          display: flex; align-items: baseline; gap: 0.75rem;
          padding: 0.85rem 1.25rem; border-bottom: 1px solid var(--border);
          font-family: var(--font-body); font-size: 0.95rem; color: var(--steel);
          background: var(--white);
        }
        .prize-list li:last-child { border-bottom: none; }
        .prize-rank {
          font-family: var(--font-display); font-weight: 700; font-size: 0.8rem;
          letter-spacing: 0.08em; text-transform: uppercase; color: var(--red);
          min-width: 5.5rem;
        }

        .golf-cta {
          background: var(--red); color: var(--white);
          padding: 2rem 2.25rem; display: flex; flex-direction: column; gap: 0.6rem;
          align-items: flex-start;
        }
        .golf-cta h3 {
          font-family: var(--font-display); font-size: 1.3rem; font-weight: 700;
          text-transform: uppercase; color: var(--white); margin: 0;
        }
        .golf-cta p { font-family: var(--font-body); font-size: 0.95rem; color: rgba(255,255,255,0.9); margin: 0; }
        .golf-cta a {
          display: inline-block; margin-top: 0.5rem;
          font-family: var(--font-display); font-weight: 700; font-size: 0.85rem;
          letter-spacing: 0.1em; text-transform: uppercase;
          color: var(--red); background: var(--white); padding: 0.75rem 1.75rem;
          text-decoration: none;
        }
        .state-box { padding: 4rem 2rem; text-align: center; font-family: var(--font-body); color: var(--muted); }
        .state-box h2 { font-family: var(--font-display); font-size: 1.4rem; font-weight: 600; text-transform: uppercase; color: var(--mid); }
      `}</style>

      <div className="golf-page">
        <div className="golf-hero">
          <div className="hero-inner">
            <p className="eyebrow">Golf Tournament</p>
            <h1>{courseName}</h1>
            <p className="hero-desc">{description}</p>
          </div>
        </div>

        {loading ? (
          <div className="state-box"><h2>Loading golf details…</h2></div>
        ) : (
          <div className="golf-body">
            <div className="facts-grid">
              <div className="fact">
                <span className="fact-label">When</span>
                <span className="fact-value">{shotgunTime} Shotgun</span>
                <span className="fact-sub">Arrive by {arrivalTime} · morning of WICnic</span>
              </div>
              <div className="fact">
                <span className="fact-label">Where</span>
                <span className="fact-value">{courseName}</span>
                <span className="fact-sub">{courseLocation}</span>
              </div>
              <div className="fact">
                <span className="fact-label">Cost</span>
                <span className="fact-value">On WIC</span>
                <span className="fact-sub">{costNote}</span>
              </div>
              <div className="fact">
                <span className="fact-label">Sign Up</span>
                <span className="fact-value">On Your RSVP</span>
                <span className="fact-sub">{signupNote}</span>
              </div>
            </div>

            {competitions.length > 0 && (
              <div className="golf-section">
                <p className="section-eyebrow">On-Course Games</p>
                {competitionsIntro && <p>{competitionsIntro}</p>}
                <div className="card-grid">
                  {competitions.map((c, i) => (
                    <div key={`${c.name}-${i}`} className="mini-card">
                      <h3>{c.name}</h3>
                      {c.note && <p>{c.note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prizes.length > 0 && (
              <div className="golf-section">
                <p className="section-eyebrow">Overall Prizes</p>
                {prizesIntro && <p>{prizesIntro}</p>}
                <ul className="prize-list">
                  {prizes.map((p, i) => (
                    <li key={`${p.rank}-${i}`}><span className="prize-rank">{p.rank}</span> {p.description}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="golf-section">
              <p className="section-eyebrow">Food &amp; Drinks</p>
              <p>{foodNote}</p>
            </div>

            <div className="golf-cta">
              <h3>Want in?</h3>
              <p>Add golf to your RSVP — check the golf box for each player in your party who wants to tee off.</p>
              <Link to="/rsvp">RSVP for Golf →</Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
